import { useEffect, useState } from "react";
import logo from "../assets/canluxa-logo.png";
/**
 * SplashScreen — Canluxa PWA
 * 
 * Usage in App.js:
 *   import SplashScreen from './components/SplashScreen';
 * 
 *   function App() {
 *     return (
 *       <AuthProvider>
 *         <SplashScreen />   ← Add this line
 *         <Router> ... </Router>
 *         <InstallPWA />
 *       </AuthProvider>
 *     );
 *   }
 */

export default function SplashScreen() {
  const [phase, setPhase] = useState("show"); // "show" | "fadeout" | "done"

  useEffect(() => {
    // Step 1: Show splash for 2s
    const fadeTimer = setTimeout(() => setPhase("fadeout"), 2000);
    // Step 2: After fade animation (0.6s), remove from DOM
    const doneTimer = setTimeout(() => setPhase("done"), 2600);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, []);

  if (phase === "done") return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600&display=swap');

        @keyframes splashLogoIn {
          0%   { opacity: 0; transform: scale(0.82) translateY(12px); }
          60%  { opacity: 1; transform: scale(1.04) translateY(-2px); }
          100% { opacity: 1; transform: scale(1)    translateY(0); }
        }
        @keyframes splashTagIn {
          0%   { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes splashDotPulse {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
          40%            { transform: scale(1);   opacity: 1;   }
        }
        @keyframes splashFadeOut {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
        @keyframes shimmerBar {
          0%   { background-position: -300% center; }
          100% { background-position:  300% center; }
        }

        .splash-root {
          position: fixed;
          inset: 0;
          z-index: 99999;
          background: #0e2112;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0;
        }
        .splash-root.fadeout {
          animation: splashFadeOut 0.6s ease forwards;
        }

        /* ── Logo card ── */
        .splash-logo-wrap {
          animation: splashLogoIn 0.7s cubic-bezier(0.34,1.56,0.64,1) forwards;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 18px;
        }

        /* The "A" icon box */
        .splash-icon-box {
          width: 88px;
          height: 88px;
          background: linear-gradient(145deg, #1a5c2a 0%, #2d8a3e 100%);
          border-radius: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow:
            0 0 0 1px rgba(74,163,93,0.25),
            0 8px 32px rgba(26,92,42,0.55),
            0 2px 8px  rgba(0,0,0,0.3);
        }

        /* Brand wordmark */
        .splash-wordmark {
          display: flex;
          align-items: baseline;
          gap: 0;
        }
        .splash-can {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 30px;
          font-weight: 600;
          color: #ffffff;
          letter-spacing: 5px;
          text-transform: uppercase;
        }
        .splash-lux {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 30px;
          font-weight: 600;
          color: #4aa35d;
          letter-spacing: 5px;
          text-transform: uppercase;
        }

        /* Tagline */
        .splash-tagline {
          animation: splashTagIn 0.5s 0.5s ease both;
          font-family: system-ui, sans-serif;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.35);
          margin-top: 4px;
        }

        /* Shimmer divider line */
        .splash-line {
          animation: splashTagIn 0.5s 0.4s ease both;
          width: 48px;
          height: 2px;
          border-radius: 1px;
          background: linear-gradient(90deg, transparent, #4aa35d, transparent);
          background-size: 300% auto;
          animation: shimmerBar 2s linear infinite, splashTagIn 0.5s 0.4s ease both;
          margin: 10px 0 6px;
        }

        /* Loading dots */
        .splash-dots {
          animation: splashTagIn 0.4s 0.9s ease both;
          display: flex;
          gap: 6px;
          margin-top: 36px;
        }
        .splash-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #4aa35d;
          animation: splashDotPulse 1.2s ease-in-out infinite;
        }
        .splash-dot:nth-child(2) { animation-delay: 0.2s; }
        .splash-dot:nth-child(3) { animation-delay: 0.4s; }
      `}</style>

      <div className={`splash-root${phase === "fadeout" ? " fadeout" : ""}`}>
        <div className="splash-logo-wrap">

          {/* Icon box with Canluxa "A" logo as SVG */}
          <div className="splash-icon-box">
            {/* <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
              
              <rect x="14" y="10" width="24" height="4" rx="2" fill="white" opacity="0.9"/>
              <rect x="21" y="7" width="10" height="4" rx="2" fill="white" opacity="0.7"/>

              <path
                d="M17 44 L26 18 L35 44"
                stroke="white"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                opacity="0.95"
              />
              <path
                d="M20.5 34 L31.5 34"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.9"
              />
              <path
                d="M26 20 C26 20 22 28 23 34 C24 37 28 37 29 34 C30 28 26 20 26 20Z"
                fill="#4aa35d"
                opacity="0.85"
              />
            </svg> */}
                      <img src={logo} alt="Canluxa" style={{ height: "100px" }} />

          </div>

          {/* Wordmark */}
          <div style={{ textAlign: "center" }}>
            <div className="splash-wordmark">
              <span className="splash-can">CAN</span>
              <span className="splash-lux">LUX</span>
              <span className="splash-can">A</span>
            </div>
            <div className="splash-line" />
            <div className="splash-tagline">Valet Trash</div>
          </div>
        </div>

        {/* Loading dots */}
        <div className="splash-dots">
          <div className="splash-dot" />
          <div className="splash-dot" />
          <div className="splash-dot" />
        </div>
      </div>
    </>
  );
}